import React, { useState, useRef, useEffect } from "react";
import { t } from "react-multi-lang";
import {
  Checkbox,
  InputField,
  SelectField,
} from "../FormElements/FormElements";
import Modal from "../Modal/Modal";

import { years } from "../../services/hoc/helpers";
import { ClassesMenu } from "../PredefinedMenus/PredefinedMenus";
import { EllipsisLoader, WhiteboxLoader } from "../Loader/Loader";

// Simple Toast Component
const SimpleToast = ({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) => {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed',
      bottom: '30px',
      right: '30px',
      padding: '12px 20px',
      backgroundColor: type === 'success' ? '#4CAF50' : '#f44336',
      color: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 10001,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '14px',
      fontWeight: '500',
      fontFamily: 'inherit',
      animation: 'fadeInUp 0.3s ease-out'
    }}>
      <span>{message}</span>
      <button onClick={onClose} style={{
        background: 'none',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        fontSize: '16px',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        opacity: 0.8
      }}>✕</button>
    </div>
  );
};

// Report Progress Modal Component with Hide Button
const ReportProgressModal = ({ show, progress, onClose, onDownload }: { 
  show: boolean; 
  progress: any; 
  onClose: () => void; 
  onDownload: () => void;
}) => {
  if (!show) return null;
  
  const isComplete = progress?.status === 'completed';
  const isFailed = progress?.status === 'failed';
  const percentage = progress?.percentage || 0;
  
  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: "#fff",
        borderRadius: "8px",
        padding: "24px",
        minWidth: "450px",
        maxWidth: "600px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        textAlign: "center"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
            {isComplete ? "✅ Report Ready!" : 
             isFailed ? " Report Failed" : 
             " Generating Report..."}
          </h3>
          {/* Hide Button - closes modal but DOES NOT stop the job */}
          <button
            onClick={() => {
              console.log('Hide button clicked - report generation continues in background');
              onClose();
            }}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#999",
              padding: "0 5px"
            }}
            title="Hide progress (generation continues)"
          >
            ✕
          </button>
        </div>
        
        {/* Progress Bar */}
        <div style={{
          width: "100%",
          backgroundColor: "#f0f0f0",
          borderRadius: "10px",
          overflow: "hidden",
          marginBottom: "15px"
        }}>
          <div style={{
            width: `${percentage}%`,
            backgroundColor: isComplete ? "#4CAF50" : isFailed ? "#f44336" : "#925b97",
            height: "35px",
            transition: "width 0.5s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: "14px",
            fontWeight: "bold"
          }}>
            {percentage > 0 && `${percentage}%`}
          </div>
        </div>
        
        {/* Statistics */}
        <div style={{ marginBottom: "20px", fontSize: "14px", color: "#555" }}>
          <p style={{ margin: "10px 0" }}>{progress?.message || "Processing..."}</p>
          <p style={{ margin: "10px 0" }}>
            Processed: <strong>{progress?.processed || 0}</strong> / {progress?.total || 0} items
          </p>
          {isComplete && (
            <p style={{ margin: "10px 0", color: "#4CAF50", fontWeight: "bold" }}>
              Report ready! Click download button below.
            </p>
          )}
        </div>
        
        {/* Buttons */}
        <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "10px" }}>
          {isComplete && (
            <button
              onClick={onDownload}
              style={{
                padding: "8px 20px",
                background: "#4CAF50",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px"
              }}
            >
              Download Report
            </button>
          )}
          
          <button
            onClick={() => {
              console.log('Close button clicked');
              onClose();
            }}
            style={{
              padding: "8px 20px",
              background: "#925b97",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px"
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default (props: {
  open: boolean;
  toggle(): any;
  link: string;
  showTo?: boolean;
}) => {
  const [classType, setClassType] = useState<any>();
  const [limits, setLimits] = useState<any>(false);
  const [balance, setBalance] = useState<any>(false);
  const [year, setYear] = useState<number>();
  const [quarter, setQuarter] = useState<"q1" | "q2" | "q3" | "q4">();
  const [eYear, setEYear] = useState<number>();
  const [eQuarter, setEQuarter] = useState<"q1" | "q2" | "q3" | "q4">();
  
  // State for progress tracking
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [showProgressModal, setShowProgressModal] = useState<boolean>(false);
  const [reportProgress, setReportProgress] = useState<any>(null);
  const [currentReportId, setCurrentReportId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  // Get report type from link
  const getReportType = (): string => {
    const link = props.link;
    // Check more specific patterns FIRST
    if (link.includes("ecl-per-account")) return "ecl-per-account";
    if (link.includes("ecl-per-cif-ccy")) return "ecl-per-cif-ccy";
    if (link.includes("ecl-per-cif")) return "ecl-per-cif";
    if (link.includes("disclosure")) return "disclosure";
    if (link.includes("cif")) return "cif";
    return "cif";
  };

  // Poll report status
  const pollReportStatus = async (reportId: string) => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
    }
    
    // const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
    const apiUrl = process.env.REACT_APP_API_URL || 'http://mbi2025.zainrohn.com/api/public';
    
    pollingInterval.current = setInterval(async () => {
      try {
        const url = `${apiUrl}/reports/status/${reportId}`;
        const response = await fetch(url);
        const data = await response.json();
        
        console.log(' Status update:', data);
        
        setReportProgress(data);
        
        if (data.status === 'completed') {
          if (pollingInterval.current) {
            clearInterval(pollingInterval.current);
            pollingInterval.current = null;
          }
          setToast({ message: '✅ Report ready for download!', type: 'success' });
          setIsGenerating(false);
        } else if (data.status === 'failed') {
          if (pollingInterval.current) {
            clearInterval(pollingInterval.current);
            pollingInterval.current = null;
          }
          setToast({ message: ` Report failed: ${data.message}`, type: 'error' });
          setIsGenerating(false);
        }
      } catch (error) {
        console.error('Failed to poll report status:', error);
      }
    }, 2000);
  };

  // Show progress modal (when hidden and job is running or complete)
  const showProgress = () => {
    setShowProgressModal(true);
  };

  // Download report and clear state
  const downloadReport = () => {
    if (currentReportId) {
      // const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      const apiUrl = process.env.REACT_APP_API_URL || 'http://mbi2025.zainrohn.com/api/public';
      window.open(`${apiUrl}/reports/download/${currentReportId}`, '_blank');
      
      // Clear all report state after download
      setReportProgress(null);
      setCurrentReportId(null);
      setShowProgressModal(false);
      setIsGenerating(false);
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    }
  };

  // Generate report
  const generateReport = async () => {
    if (!year || !quarter) {
      setToast({ message: ' Please select year and quarter', type: 'error' });
      return;
    }
    
    // Clear any previous report state
    setReportProgress(null);
    setCurrentReportId(null);
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
    
    // Close the configuration modal FIRST
    props.toggle();
    
    setIsGenerating(true);
    setShowProgressModal(true);
    setReportProgress({
      total: 0,
      processed: 0,
      percentage: 0,
      status: 'processing',
      message: 'Starting report generation...'
    });
    
    const reportType = getReportType(); 
    // const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
    const apiUrl = process.env.REACT_APP_API_URL || 'http://mbi2025.zainrohn.com/api/public';
    
    // Build request body
    const requestBody: any = {
      quarter: quarter,
      year: year,
      limits: limits ? "yes" : "no"
    };
    
    if (classType?.value) {
      requestBody.class_type = classType.value;
    }
    
    if (balance) {
      requestBody.balance = balance;
    }
    
    if (props.showTo && eYear && eQuarter) {
      requestBody.quarter2 = eQuarter;
      requestBody.year2 = eYear;
    }
    
    try {
      const url = `${apiUrl}/reports/generate/${reportType}`;
      console.log('🚀 Generating report:', url, requestBody);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify(requestBody)
      });
      
      const result = await response.json();
      console.log(' Generate response:', result);
      
      if (result.success) {
        setCurrentReportId(result.report_id);
        pollReportStatus(result.report_id);
        setToast({ message: ' Report generation started!', type: 'success' });
      } else {
        setToast({ message: ` Failed: ${result.message}`, type: 'error' });
        setShowProgressModal(false);
        setIsGenerating(false);
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      setToast({ message: ' Failed to generate report', type: 'error' });
      setShowProgressModal(false);
      setIsGenerating(false);
    }
  };

  // Close progress modal (hide it, but keep progress for floating button)
  const closeProgressModal = () => {
    setShowProgressModal(false);
    // Don't clear progress - keep it for floating button
  };

  // Get link for backward compatibility (direct download)
  const getLink = () => {
    if (year && quarter && (!props.showTo || (eYear && eQuarter))) {
      let url = `${props.link}?quarter1=${quarter}&year1=${year}&quarter2=${eQuarter}&year2=${eYear}`;

      if (!props.link.includes("disclosure")) {
        url += `&class_type=${classType?.value || ""}&limits=${
          limits ? "yes" : "no"
        }`;
      }
      if (props.link.includes("disclosure")) {
        url += `&limits=no`;
      }

      return url;
    }
    return "#";
  };

  // Show floating button when modal is hidden and report is processing OR completed
  const showFloatingButton = reportProgress && 
    (reportProgress.status === 'processing' || reportProgress.status === 'completed') && 
    !showProgressModal;

  // Get button text based on progress status
  const getFloatingButtonText = () => {
    if (!reportProgress) return "Generating...";
    if (reportProgress.status === 'completed') {
      return `✅ Download Report (${reportProgress.percentage}%)`;
    }
    return ` Generating... (${reportProgress.percentage}%)`;
  };

  // Get button style based on progress status
  const getFloatingButtonStyle = () => {
    if (reportProgress?.status === 'completed') {
      return {
        backgroundColor: "#4CAF50",
        padding: "10px 15px",
      };
    }
    return {
      backgroundColor: "#925b97",
      padding: "10px 15px",
    };
  };

  return (
    <>
      {/* Toast Notification */}
      {toast && (
        <SimpleToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Floating Progress Button - appears when modal is hidden and report is processing OR ready */}
      {showFloatingButton && (
        <button
          onClick={() => {
            if (reportProgress?.status === 'completed') {
              // If report is ready, download and clear
              downloadReport();
            } else {
              // Otherwise show progress modal
              showProgress();
            }
          }}
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            padding: getFloatingButtonStyle().padding,
            backgroundColor: getFloatingButtonStyle().backgroundColor,
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            zIndex: 9999,
            fontSize: "14px",
            fontWeight: "bold",
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
            transition: "all 0.3s ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "0.9";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
        >
          {getFloatingButtonText()}
        </button>
      )}
      
      {/* Report Progress Modal */}
      <ReportProgressModal
        show={showProgressModal}
        progress={reportProgress}
        onClose={closeProgressModal}
        onDownload={downloadReport}
      />
      
      <Modal open={props.open} toggle={props.toggle}>
        <div style={{ minWidth: 500, textAlign: "left" }}>
          <h2>Report Configurations</h2>
          <hr
            style={{
              border: "none",
              borderTop: "1px solid #DDD",
              marginBottom: 30,
            }}
          />
          <form action="">
            {!props.link.split("/").includes("disclosure") && (
              <div className="filter">
                <ClassesMenu
                  isClearable
                  value={classType}
                  onChange={(selected: any) => {
                    setClassType(selected);
                  }}
                  placeholder="Class Type"
                />

                <Checkbox
                  label="Unused Limits"
                  style={{ margin: "20px 0" }}
                  checked={limits}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setLimits(e.target.checked)
                  }
                />
              </div>
            )}

            {props.showTo && <div className="sep text-center">FROM</div>}
            <div className="config">
              <SelectField
                defaultValue={year ? { label: year, value: year } : undefined}
                onChange={(selected: { value: number }) =>
                  setYear(selected?.value)
                }
                placeholder={t("year")}
                options={years}
              />
            </div>
            <div className="config">
              <SelectField
                defaultValue={
                  quarter
                    ? { label: quarter?.toUpperCase(), value: quarter }
                    : undefined
                }
                onChange={(selected: { value: "q1" | "q2" | "q3" | "q4" }) =>
                  setQuarter(selected?.value)
                }
                placeholder={t("quarter")}
                options={[
                  { label: "Q1", value: "q1" },
                  { label: "Q2", value: "q2" },
                  { label: "Q3", value: "q3" },
                  { label: "Q4", value: "q4" },
                ]}
              />
            </div>
            {props.showTo && (
              <>
                <div className="sep text-center">TO</div>
                <div className="config">
                  <SelectField
                    defaultValue={
                      eYear ? { label: eYear, value: eYear } : undefined
                    }
                    onChange={(selected: { value: number }) =>
                      setEYear(selected?.value)
                    }
                    placeholder={t("year")}
                    options={years}
                  />
                </div>
                <div className="config">
                  <SelectField
                    defaultValue={
                      eQuarter
                        ? { label: eQuarter?.toUpperCase(), value: eQuarter }
                        : undefined
                    }
                    onChange={(selected: { value: "q1" | "q2" | "q3" | "q4" }) =>
                      setEQuarter(selected?.value)
                    }
                    placeholder={t("quarter")}
                    options={[
                      { label: "Q1", value: "q1" },
                      { label: "Q2", value: "q2" },
                      { label: "Q3", value: "q3" },
                      { label: "Q4", value: "q4" },
                    ]}
                  />
                </div>
              </>
            )}
            <br />
            <div className="text-center">
              <button
                onClick={generateReport}
                disabled={isGenerating}
                className="button bg-gold color-white"
                style={{ 
                  padding: "15px 20px",
                  opacity: isGenerating ? 0.6 : 1,
                  cursor: isGenerating ? "not-allowed" : "pointer"
                }}
              >
                {isGenerating ? "Generating..." : "View Report"}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};